import React, { useState, useEffect, useCallback } from 'react';
import TranslatorInput from './components/TranslatorInput';
import TranslationOutput from './components/TranslationOutput';
import ModelSelector from './components/ModelSelector';
import { useDebounce } from './hooks/useDebounce';
import { translateAndGenerateHeatmap } from './services/geminiService';

type ModelType = 'bagdaunov' | 'transformer';

interface ModelStats {
  bleu: number;
  epochs: number;
}

const modelData: Record<ModelType, ModelStats> = {
  bagdaunov: { bleu: 7.08, epochs: 40 },
  transformer: { bleu: 0, epochs: 20 },
};


const App: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [russianText, setRussianText] = useState('');
  const [heatmapUrl, setHeatmapUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelType>('bagdaunov');

  const debouncedInputText = useDebounce(inputText, 1000);

  const handleTranslation = useCallback(async () => {
    if (!debouncedInputText.trim()) {
      setRussianText('');
      setHeatmapUrl('');
      setError(null);
      return;
    }

    // if (selectedModel === 'transformer') {
    //   setRussianText('"Transformers" based translator is coming soon.');
    //   setHeatmapUrl('');
    //   setIsLoading(false);
    //   setError(null);
    //   return;
    // }

    setIsLoading(true);
    setError(null);

    try {
      const { translation, imageUrl } = await translateAndGenerateHeatmap(debouncedInputText, selectedModel);
      setRussianText(translation);
      setHeatmapUrl(imageUrl);
    } catch (err) {
      console.error(err);
      setError('Failed to get translation. Please try again.');
      setRussianText('');
      setHeatmapUrl('');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedInputText, selectedModel]);

  useEffect(() => {
    handleTranslation();
  }, [debouncedInputText, selectedModel, handleTranslation]);

  const currentStats = modelData[selectedModel];

  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <header className="w-full max-w-6xl text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">AI Translator</h1>
          <p className="text-lg text-gray-600 mt-2">English to Russian with Attention Visualization</p>
        </header>

        <div className="flex justify-center mb-8">
            <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4">
                    <h2 className="text-sm font-semibold text-gray-500">BLEU Score</h2>
                    <p className="text-2xl font-bold text-pink-500">{currentStats.bleu}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4">
                    <h2 className="text-sm font-semibold text-gray-500">Epochs Trained</h2>
                    <p className="text-2xl font-bold text-pink-500">{currentStats.epochs}</p>
                </div>
            </div>
        </div>

        <div className="mb-8 flex justify-center">
          <ModelSelector selectedModel={selectedModel} onModelChange={setSelectedModel} />
        </div>

        <main className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="w-full">
            <TranslatorInput value={inputText} onChange={(e) => setInputText(e.target.value)} />
          </div>
          <div className="w-full">
            <TranslationOutput
              russianText={russianText}
              heatmapUrl={heatmapUrl}
              isLoading={isLoading}
              error={error}
              hasInput={!!inputText.trim()}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
